import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  BookOpen, Plus, ChevronRight, ArrowLeft, Edit3, Layers,
  AlertTriangle, CheckCircle2, Wand2,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Input, Textarea, Select, Spinner, EmptyState,
} from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

// =============================================================================
// Rate Cards — SoR library
// =============================================================================
// Enum values confirmed from discovery CSV:
//   category: fixed | labour | lafha | markup | materials | other | plant | subcontract
//   unit:     cubic_metre | day | each | hour | lump_sum | metre | month |
//             per_site | percent | square_metre | week
// =============================================================================

const CATEGORIES = [
  "labour",
  "materials",
  "plant",
  "subcontract",
  "markup",
  "lafha",
  "fixed",
  "other",
] as const;

const UNITS = [
  "hour",
  "day",
  "week",
  "month",
  "each",
  "metre",
  "square_metre",
  "cubic_metre",
  "per_site",
  "lump_sum",
  "percent",
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  labour:      "Labour",
  materials:   "Materials",
  plant:       "Plant & Equipment",
  subcontract: "Subcontract",
  markup:      "Markup rates",
  lafha:       "LAFHA",
  fixed:       "Fixed fees",
  other:       "Other",
};

// -----------------------------------------------------------------------------
// List screen
// -----------------------------------------------------------------------------
export function RateCardsListScreen() {
  const user = useRequireAuth();
  const [filter, setFilter] = useState<"active" | "all" | "default" | "client">("active");

  const { data: cards = [], isLoading } = useQuery({
    queryKey: ["rate_cards", filter],
    queryFn: async () => {
      let q = supabase
        .from("rate_cards")
        .select(`
          rate_card_id, rate_card_code, display_name, description,
          client_id, is_default, is_active,
          effective_from, effective_until,
          clients(client_code, legal_name)
        `)
        .order("is_default", { ascending: false })
        .order("display_name");
      if (filter === "active")  q = q.eq("is_active", true);
      if (filter === "default") q = q.eq("is_default", true);
      if (filter === "client")  q = q.not("client_id", "is", null);
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
      <Link to="/finance" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Finance hub
      </Link>
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Rate Cards</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Schedule of Rates libraries. Used by the Quote builder to price line items.
          </div>
        </div>
        {hasRole(user, "manager") && (
          <div className="flex gap-2">
            <Link to="/rates/import-pdf"><Button variant="secondary" icon={Wand2}>Import from PDF</Button></Link>
            <Link to="/rates/new"><Button icon={Plus}>New Rate Card</Button></Link>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        {([
          ["active",  "Active"],
          ["all",     "All"],
          ["default", "Default"],
          ["client",  "Client-specific"],
        ] as const).map(([v, label]) => (
          <button
            key={v}
            onClick={() => setFilter(v)}
            className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border transition-colors ${
              filter === v ? "bg-accent/12 text-accent border-accent/30" : "bg-ink-raised text-text-muted border-line"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : cards.length === 0 ? (
        <Card><EmptyState title="No rate cards" description="Create a rate card to manage SoR line items." icon={BookOpen} /></Card>
      ) : (
        <div className="space-y-2">
          {cards.map(rc => (
            <Link key={rc.rate_card_id} to={`/rates/${rc.rate_card_id}`}>
              <Card>
                <div className="p-4 flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                    rc.is_default ? "bg-accent/12 text-accent" : "bg-ink-raised text-text-muted"
                  }`}>
                    <Layers size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[11px] text-accent">{rc.rate_card_code}</span>
                      {rc.is_default && <Pill label="default" tone="bid" small />}
                      {!rc.is_active && <Pill label="inactive" tone="lost" small />}
                    </div>
                    <div className="text-[14px] text-text mt-0.5 truncate">{rc.display_name}</div>
                    <div className="text-[11px] font-mono text-text-muted mt-0.5">
                      {rc.clients?.legal_name ?? "(all clients — default card)"}
                      {rc.effective_from && ` · from ${fmtDate(rc.effective_from)}`}
                      {rc.effective_until && ` to ${fmtDate(rc.effective_until)}`}
                    </div>
                    {rc.description && (
                      <div className="text-[12px] text-text-muted mt-1 truncate">{rc.description}</div>
                    )}
                  </div>
                  <ChevronRight size={14} className="text-text-dim mt-2" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Detail screen
// -----------------------------------------------------------------------------
export function RateCardDetailScreen() {
  const { rateCardId } = useParams<{ rateCardId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const canManage = hasRole(user, "manager");
  const [editingLineId, setEditingLineId] = useState<string | null>(null);

  const { data: card, isLoading: cardLoading } = useQuery({
    queryKey: ["rate_card", rateCardId],
    enabled: !!rateCardId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rate_cards")
        .select(`
          rate_card_id, rate_card_code, display_name, description,
          client_id, is_default, is_active,
          effective_from, effective_until, notes, updated_at,
          clients(client_code, legal_name)
        `)
        .eq("rate_card_id", rateCardId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: lines = [], isLoading: linesLoading, refetch: refetchLines } = useQuery({
    queryKey: ["rate_lines", rateCardId],
    enabled: !!rateCardId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rate_lines")
        .select(`
          rate_line_id, rate_card_id, line_code, display_name,
          category, unit, cost_price_ex_gst, sell_price_ex_gst,
          default_markup_percent, notes, sort_order, is_active
        `)
        .eq("rate_card_id", rateCardId!)
        .order("category")
        .order("sort_order")
        .order("line_code");
      if (error) throw error;
      return data as any[];
    },
  });

  if (cardLoading || linesLoading) return <div className="py-20 flex justify-center"><Spinner /></div>;
  if (!card) return <div className="p-6">Rate card not found.</div>;

  const byCategory = lines.reduce((acc, l) => {
    (acc[l.category] ||= []).push(l);
    return acc;
  }, {} as Record<string, any[]>);

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
      <button onClick={() => navigate("/rates")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> Rate Cards
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[13px] text-accent">{card.rate_card_code}</span>
            {card.is_default && <Pill label="default" tone="bid" />}
            {!card.is_active && <Pill label="inactive" tone="lost" />}
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight mt-1">{card.display_name}</h1>
          <div className="text-text-muted text-[13px] mt-1">
            {card.clients?.legal_name ?? "All clients (default)"}
            {card.effective_from && ` · from ${fmtDate(card.effective_from)}`}
            {card.effective_until && ` to ${fmtDate(card.effective_until)}`}
          </div>
          {card.description && (
            <div className="text-text-muted text-[13px] mt-2">{card.description}</div>
          )}
        </div>
        {canManage && (
          <Button icon={Plus} onClick={() => setEditingLineId("new")}>Add line</Button>
        )}
      </div>

      <Card>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Total lines" value={lines.length} />
          <Stat label="Active" value={lines.filter(l => l.is_active).length} />
          <Stat label="Categories" value={Object.keys(byCategory).length} />
          <Stat label="Updated" value={card.updated_at ? fmtDate(card.updated_at) : "—"} />
        </div>
      </Card>

      <div className="mt-5 space-y-5">
        {(CATEGORIES as readonly string[]).filter(c => byCategory[c]).map(cat => (
          <CategorySection
            key={cat}
            label={CATEGORY_LABELS[cat] ?? cat}
            lines={byCategory[cat]}
            canEdit={canManage}
            onEdit={(id) => setEditingLineId(id)}
          />
        ))}
      </div>

      {editingLineId && (
        <RateLineEditModal
          rateCardId={card.rate_card_id}
          lineId={editingLineId === "new" ? null : editingLineId}
          existingLines={lines}
          onClose={() => setEditingLineId(null)}
          onSaved={() => { refetchLines(); setEditingLineId(null); }}
        />
      )}
    </div>
  );
}

function CategorySection({ label, lines, canEdit, onEdit }: {
  label: string;
  lines: any[];
  canEdit: boolean;
  onEdit: (lineId: string) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12px] font-medium text-text-muted uppercase tracking-wider">
          {label} <span className="text-text-dim">· {lines.length} lines</span>
        </div>
      </div>
      <Card>
        <div className="divide-y divide-line">
          <div className="grid grid-cols-[110px_1fr_90px_110px_110px_80px_40px] gap-3 px-4 py-2 text-[10px] font-medium text-text-muted uppercase tracking-wider">
            <div>Code</div>
            <div>Description</div>
            <div>Unit</div>
            <div className="text-right">Cost</div>
            <div className="text-right">Sell</div>
            <div className="text-right">Markup</div>
            <div></div>
          </div>
          {lines.map(line => (
            <div key={line.rate_line_id} className={`grid grid-cols-[110px_1fr_90px_110px_110px_80px_40px] gap-3 px-4 py-3 items-center hover:bg-ink-raised/50 ${!line.is_active ? "opacity-50" : ""}`}>
              <div className="font-mono text-[11px] text-accent truncate" title={line.line_code}>{line.line_code}</div>
              <div className="text-[13px] text-text truncate" title={line.display_name}>{line.display_name}</div>
              <div className="text-[11px] font-mono text-text-muted">{line.unit}</div>
              <div className="text-[13px] font-mono text-text-muted text-right">{fmtMoney(line.cost_price_ex_gst)}</div>
              <div className="text-[13px] font-mono text-text text-right">{fmtMoney(line.sell_price_ex_gst)}</div>
              <div className="text-[12px] font-mono text-text-muted text-right">
                {line.default_markup_percent != null ? `${line.default_markup_percent}%` : "—"}
              </div>
              <div className="text-right">
                {canEdit && (
                  <button onClick={() => onEdit(line.rate_line_id)} className="text-text-muted hover:text-accent" title="Edit line">
                    <Edit3 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-text-muted font-medium">{label}</div>
      <div className="text-[20px] font-mono text-text mt-1">{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Edit modal — create or update a rate line
// -----------------------------------------------------------------------------
function RateLineEditModal({
  rateCardId, lineId, existingLines, onClose, onSaved,
}: {
  rateCardId: string;
  lineId: string | null;
  existingLines: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !lineId;
  const existing = lineId ? existingLines.find(l => l.rate_line_id === lineId) : null;

  const [form, setForm] = useState({
    line_code:              existing?.line_code ?? "",
    display_name:           existing?.display_name ?? "",
    category:               existing?.category ?? "labour",
    unit:                   existing?.unit ?? "hour",
    cost_price_ex_gst:      String(existing?.cost_price_ex_gst ?? "0"),
    sell_price_ex_gst:      String(existing?.sell_price_ex_gst ?? "0"),
    default_markup_percent: existing?.default_markup_percent != null ? String(existing.default_markup_percent) : "",
    notes:                  existing?.notes ?? "",
    sort_order:             String(existing?.sort_order ?? 0),
    is_active:              existing?.is_active ?? true,
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!form.line_code.trim())    throw new Error("Line code is required");
      if (!form.display_name.trim()) throw new Error("Display name is required");

      const payload = {
        rate_card_id:           rateCardId,
        line_code:              form.line_code.trim(),
        display_name:           form.display_name.trim(),
        category:               form.category,
        unit:                   form.unit,
        cost_price_ex_gst:      parseFloat(form.cost_price_ex_gst) || 0,
        sell_price_ex_gst:      parseFloat(form.sell_price_ex_gst) || 0,
        default_markup_percent: form.default_markup_percent ? parseFloat(form.default_markup_percent) : null,
        notes:                  form.notes.trim() || null,
        sort_order:             parseInt(form.sort_order, 10) || 0,
        is_active:              form.is_active,
      };

      if (isNew) {
        const { error } = await supabase.from("rate_lines").insert(payload);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("rate_lines").update(payload).eq("rate_line_id", lineId!);
        if (error) throw error;
      }
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center" onClick={onClose}>
      <div className="bg-ink-card border border-line w-full md:max-w-xl md:rounded-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line">
          <h3 className="text-lg font-semibold">{isNew ? "New rate line" : "Edit rate line"}</h3>
          <div className="text-[12px] text-text-muted mt-1">
            {isNew ? "Add a line to the rate card for use in quotes." : "Changes apply to new quotes. Existing quote lines keep their captured prices."}
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Line code">
              <Input mono value={form.line_code} onChange={e => setForm({...form, line_code: e.target.value})} placeholder="e.g. LAB-CAB-HR" />
            </Field>
            <Field label="Category">
              <Select value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c] ?? c}</option>)}
              </Select>
            </Field>
          </div>

          <Field label="Display name">
            <Input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} placeholder="e.g. Cabler — hourly" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Unit">
              <Select value={form.unit} onChange={e => setForm({...form, unit: e.target.value})}>
                {UNITS.map(u => <option key={u} value={u}>{u.replace(/_/g, " ")}</option>)}
              </Select>
            </Field>
            <Field label="Sort order">
              <Input type="number" mono value={form.sort_order} onChange={e => setForm({...form, sort_order: e.target.value})} />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Cost (ex-GST)">
              <Input type="number" step="0.01" mono value={form.cost_price_ex_gst} onChange={e => setForm({...form, cost_price_ex_gst: e.target.value})} />
            </Field>
            <Field label="Sell (ex-GST)">
              <Input type="number" step="0.01" mono value={form.sell_price_ex_gst} onChange={e => setForm({...form, sell_price_ex_gst: e.target.value})} />
            </Field>
            <Field label="Markup %">
              <Input type="number" step="0.01" mono value={form.default_markup_percent} onChange={e => setForm({...form, default_markup_percent: e.target.value})} placeholder="Optional" />
            </Field>
          </div>

          <Field label="Notes">
            <Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional internal notes" />
          </Field>

          <label className="flex items-center gap-2 text-[13px] text-text">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="accent-accent" />
            Active (available for new quotes)
          </label>

          {saveMut.isError && (
            <div className="bg-danger/10 border border-danger/20 rounded px-3 py-2 text-[12px] text-danger">
              <AlertTriangle size={11} className="inline mr-1" />
              {String((saveMut.error as any)?.message ?? saveMut.error)}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button icon={CheckCircle2} onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? "Saving…" : "Save line"}
          </Button>
        </div>
      </div>
    </div>
  );
}
